"use client";

import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { Activity, Calendar, Stethoscope } from "lucide-react";

import { animateChildren } from "@/lib/animation-utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ReproductionCycleStep() {
  const { control, formState: { dirtyFields } } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        (child as HTMLElement).style.opacity = "0";
        (child as HTMLElement).style.transform = "translateY(20px)";
      });

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          );
        }
      });
    }
  }, []);

  const years = Array.from({ length: 10 }, (_, i) =>
    (new Date().getFullYear() - 5 + i).toString(),
  );
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const bcsOptions = [
    "1.0",
    "1.5",
    "2.0",
    "2.5",
    "3.0",
    "3.5",
    "4.0",
    "4.5",
    "5.0",
  ];
  const diagnosisOptions = [
    "Confirmed",
    "Suspected",
    "Not Pregnant",
    "Inconclusive",
  ];

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-[#008c8fff]">
          Reproduction Cycle
        </h2>
        <p className="text-[#015354ff]">
          Enter details about the cow&#39;s reproduction cycle
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField
          control={control}
          name="syncYear"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Calendar className="h-4 w-4" />
                Sync Year
                {dirtyFields.syncYear && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select
                value={field.value ? String(field.value) : undefined}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {years.map((year) => (
                    <SelectItem
                      key={year}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(year)}
                    >
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.syncYear && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="syncMonth"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Calendar className="h-4 w-4" />
                Sync Month
                {dirtyFields.syncMonth && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select
                value={field.value ? String(field.value) : undefined}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {months.map((month) => (
                    <SelectItem
                      key={month}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(month)}
                    >
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.syncMonth && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="estrousCycle"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Activity className="h-4 w-4" />
                Estrous Cycle
                {dirtyFields.estrousCycle && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Input placeholder="Enter cycle details" {...field} />
              </FormControl>
              {dirtyFields.estrousCycle && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="bcs"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Activity className="h-4 w-4" />
                Body Condition Score (BCS)
                {dirtyFields.bcs && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select
                value={field.value ? String(field.value) : undefined}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select BCS" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {bcsOptions.map((score) => (
                    <SelectItem
                      key={score}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(score)}
                    >
                      {score}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.bcs && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="pregnancyDiagnosis"
          render={({ field }) => (
            <FormItem className="md:col-span-2">
              <FormLabel className="flex items-center gap-2 text-[#008c8fff] flex-wrap">
                <Stethoscope className="h-4 w-4" />
                Pregnancy Diagnosis
                {dirtyFields.pregnancyDiagnosis && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select
                value={field.value ? String(field.value) : undefined}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select diagnosis" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {diagnosisOptions.map((option) => (
                    <SelectItem
                      key={option}
                      className={"overflow-y-auto z-50 background-white"}
                      value={String(option)}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dirtyFields.pregnancyDiagnosis && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
